/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React from "react";
import { Image, Button, Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { ExternalLinkIcon } from "@heroicons/react/solid";
import * as S from "./styles";

export interface IHeroSectionProps {
  heroSectionId?: string;
  heroHeading1?: string;
  heroHeading2?: string;
  heroHeadingBadge?: string;
  heroDescription?: string;
  heroDarkButtonItem?: {
    name: string;
    link: string;
  };
  heroLightButtonItem?: {
    name: string;
    link: string;
  };
  heroBrand?: {
    name: string;
    logo: string;
    link: string;
    footerText?: string;
    height?: number;
    width?: number;
  };
  heroWatermark?: {
    name?: string;
    image: string;
    height?: number;
    width?: number;
  };
}

export const HeroSection = ({
  heroSectionId,
  heroHeading1,
  heroHeading2,
  heroHeadingBadge,
  heroDescription,
  heroDarkButtonItem,
  heroLightButtonItem,
  heroBrand,
  heroWatermark,
}: IHeroSectionProps) => {
  const _renderHeadingIf = () => {
    if (heroHeading1) {
      return (
        <S.Heading className="fw-bolder text-secondary">
          {heroHeading1}
          {heroHeading2 && (
            <span className="d-none d-sm-inline">{` ${heroHeading2}`}</span>
          )}
          {heroHeadingBadge && (
            <span
              className="pro-badge fw-bolder"
              style={{ textShadow: "none" }}
            >
              {heroHeadingBadge}
            </span>
          )}
        </S.Heading>
      );
    }
  };
  const _renderDescriptionIf = () => {
    if (heroDescription) {
      return (
        <S.Description className="fw-light mb-5 h5">
          {heroDescription}
        </S.Description>
      );
    }
  };
  const _renderDescriptionButtonsIf = () => {
    return (
      <div className="d-flex justify-content-center mb-5">
        {heroDarkButtonItem && (
          <Button
            variant="secondary"
            as={Link}
            to={heroDarkButtonItem?.link}
            className="text-dark me-3"
          >
            {heroDarkButtonItem?.name}{" "}
            <ExternalLinkIcon className="icon icon-xs d-none d-sm-inline ms-1" />
          </Button>
        )}
        {heroLightButtonItem && (
          <Button
            variant="outline-secondary"
            as={HashLink}
            to={heroLightButtonItem?.link}
            className="d-flex align-items-center"
          >
            {heroLightButtonItem?.name}
          </Button>
        )}
      </div>
    );
  };
  const _renderBrandIf = () => {
    return (
      <div className="text-center mb-6 mb-lg-5">
        {heroBrand?.logo && (
          <a href={heroBrand?.link} target="_blank" rel="noopener noreferrer">
            <Image
              src={heroBrand?.logo}
              height={heroBrand?.height ?? 25}
              width={heroBrand?.width ?? 25}
              className="mb-3"
              alt={heroBrand.name}
            />
            {heroBrand?.footerText && (
              <p className="text-muted font-small m-0">
                {heroBrand.footerText}
              </p>
            )}
          </a>
        )}
      </div>
    );
  };

  const _renderWatermarkImageIf = () => {
    if (heroWatermark?.image) {
      return (
        <div className="react-homepage-icon d-none d-lg-block">
          <Image
            src={heroWatermark?.image}
            height={heroWatermark?.height ?? 700}
            width={heroWatermark?.width ?? 600}
            className="mb-3"
            alt={heroWatermark?.name ?? "watermark"}
          />
        </div>
      );
    }
  };
  return (
    <S.Section
      className="section-header pt-5 pt-lg-6 pb-9 pb-lg-12 overflow-hidden"
      id={heroSectionId}
      bgImg={heroWatermark?.image}
      height={heroWatermark?.height}
    >
      <Container>
        <Row>
          <Col xs={12} className="text-center">
            {_renderHeadingIf()}
            {_renderDescriptionIf()}
            {_renderDescriptionButtonsIf()}
            {/* {_renderBrandIf()} */}
          </Col>
        </Row>
        {/* {_renderWatermarkImageIf()} */}
      </Container>
    </S.Section>
  );
};

HeroSection.defaultProps = {
  heroSectionId: "#home",
};
